import React from "react";
import { Input } from "reactstrap";
import Select from "react-select";
import Switch from "react-switch";
import { Editor } from "@tinymce/tinymce-react";
import InputMask from "react-input-mask";
import { FiEdit3 } from "react-icons/fi";

const DynamicFieldRenderer = ({
  fieldKey,
  formik,
  isRequired,
  inputStyle,
  inputStyleDate,
  styleSelect,
  editorRef,
  FIELD_DIMENSIONS,
  OnSymbol,
  Offsymbol,
  renderUsersOptions,
  renderCategoriesOptions,
  renderPeriodOptions,
  renderTermesOptions,
  renderIndexationOptions,
  handleCguChange,
  handleContratChange,
  api,
  t,
}) => {
  const key = fieldKey;

  // Liste des champs par type
  const fileFields = [t("CGU"), t("Contrat")];
  const dateFields = [
    t("Date de démarrage"),
    t("Date historisation"),
    t("Date contrat"),
  ];
  const switchFields = [
    t("Alerte SMS"),
    t("Facturation auto ?"),
    t("Report facture"),
    t("Paiement en ligne ?"),
    t("Blocage auto"),
    t("Application bloquée ?"),
  ];
  const editorFields = [
    t("Informations prestataire"),
    t("Informations complémentaires"),
    t("Signature email"),
    t("Chèque (adresse)"),
  ];
  const selectFields = [
    t("Contact de facturation"),
    t("Périodicité de facturation"),
    t("Terme"),
    t("Indice contrat"),
    t("Indice précédent"),
    t("Indice courant"),
  ];
  const textareaFields = [t("Footer PDF"), t("Virement (RIB)")];

  // === Champs de type FILE ===
  if (fileFields.includes(key)) {
    return (
      <div className="input-wrapper position-relative">
        <Input
          className="form-control-sm shadow-sm border-0"
          type="file"
          name={key}
          onChange={key === t("CGU") ? handleCguChange : handleContratChange}
          style={inputStyle(isRequired)}
        />
        <FiEdit3
          size={12}
          className="position-absolute text-muted"
          style={{
            right: "8px",
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            opacity: 0.9,
          }}
        />
      </div>
    );
  }

  // === Champs de type DATE ===
  if (dateFields.includes(key)) {
    // Convertir dd/mm/yyyy vers yyyy-mm-dd pour l'input type="date"
    const convertToISO = (dateStr) => {
      if (!dateStr) return "";
      const [day, month, year] = dateStr.split("/");
      return `${year}-${month}-${day}`;
    };
    return (
      <div className="input-wrapper position-relative">
        <InputMask
          className="form-control form-control-sm shadow-sm border-0"
          type="date"
          placeholder="jj/mm/aaaa"
          name={key}
          value={convertToISO(formik.values[key])}
          onChange={(e) => {
            // Convertir yyyy-mm-dd vers dd/mm/yyyy pour stocker
            const [year, month, day] = e.target.value.split("-");
            formik.setFieldValue(key, `${day}/${month}/${year}`);
          }}
          onBlur={formik.handleBlur}
          style={inputStyleDate(isRequired)}
        />
      </div>
    );
  }

  // === Champs de type SWITCH (boolean) ===
  if (switchFields.includes(key)) {
    return (
      <div
        className="switch-wrapper d-flex align-items-center"
        style={{
          height: FIELD_DIMENSIONS.height,
          minHeight: FIELD_DIMENSIONS.minHeight,
          padding: "0 0px",
          backgroundColor: "#fff",
          borderRadius: FIELD_DIMENSIONS.borderRadius,
          transition: "all 0.2s ease",
        }}
      >
        <Switch
          uncheckedIcon={<Offsymbol />}
          checkedIcon={<OnSymbol />}
          onColor={localStorage.getItem("color_" + api) || "#555B21"}
          offColor="#6c757d"
          onHandleColor="#fff"
          offHandleColor="#fff"
          handleDiameter={16}
          borderRadius={10}
          boxShadow="none"
          activeBoxShadow="0 0 2px 4px rgba(0, 123, 255, 0.1)"
          onChange={(option) => formik.setFieldValue(key, option)}
          checked={formik.values[key] == 1 ? true : false}
          className="uniform-switch"
        />
      </div>
    );
  }

  // === Champs de type EDITOR (TinyMCE) ===
  if (editorFields.includes(key)) {
    return (
      <div style={{ zIndex: 50000 }}>
        <Editor
          onEditorChange={(content) => {
            formik.setFieldValue(key, content);
          }}
          apiKey="qtpzuvciq1zhh5k87yw2b2hoie40flpoqkafr142u8lfhp6i"
          onInit={(evt, editor) => (editorRef.current = editor)}
          value={formik.values[key]}
          init={{
            height: 300,
            menubar: false,
            plugins: [
              "advlist",
              "autolink",
              "lists",
              "link",
              "image",
              "charmap",
              "preview",
              "anchor",
              "searchreplace",
              "visualblocks",
              "code",
              "fullscreen",
              "insertdatetime",
              "media",
              "table",
              "code",
              "help",
              "wordcount",
              "autosave",
            ],
            toolbar:
              "undo redo | blocks | " +
              "bold italic forecolor | alignleft aligncenter " +
              "alignright alignjustify | bullist numlist outdent indent | " +
              "removeformat | help",
            content_style:
              "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
          }}
        />
      </div>
    );
  }

  // === Champs de type SELECT ===
  if (selectFields.includes(key)) {
    let options = [];
    if (key === t("Contact de facturation")) {
      options = renderUsersOptions();
    } else if (key === t("Périodicité de facturation")) {
      options = renderPeriodOptions();
    } else if (key === t("Terme")) {
      options = renderTermesOptions();
    } else {
      options = renderIndexationOptions();
    }

    return (
      <div className="select-wrapper">
        <Select
          classNamePrefix="select2-selection"
          styles={styleSelect(isRequired)}
          options={options}
          name={key}
          value={formik.values[key] || null}
          onChange={(option) => formik.setFieldValue(key, option)}
          onBlur={formik.handleBlur}
          isClearable={true}
          placeholder={`Sélectionner ${key.toLowerCase()}...`}
          menuPortalTarget={document.body}
        />
      </div>
    );
  }

  // === Champs de type TEXTAREA ===
  if (textareaFields.includes(key)) {
    return (
      <div className="input-wrapper position-relative">
        <Input
          className="form-control-sm shadow-sm border-0"
          type="textarea"
          rows={key == t("Footer PDF") ? "17" : "4"}
          name={key}
          value={formik.values[key] ?? ""}
          onChange={formik.handleChange}
        />
        <FiEdit3
          size={12}
          className="position-absolute text-muted"
          style={{
            right: "8px",
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            opacity: 0.9,
          }}
        />
      </div>
    );
  }

  // === Champs de type TEXT par défaut ===
  return (
    <div className="input-wrapper position-relative">
      <Input
        className={`form-control-sm shadow-sm border-0 ${
          formik.touched[key] && formik.errors[key] ? "is-invalid" : ""
        }`}
        type={key === t("TVA") ? "number" : "text"}
        name={key}
        value={formik.values[key] ?? ""}
        onChange={formik.handleChange}
        style={inputStyle(isRequired)}
      />
      <FiEdit3
        size={12}
        className="position-absolute text-muted"
        style={{
          right: "8px",
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          opacity: 0.9,
        }}
      />
    </div>
  );
};

export default DynamicFieldRenderer;
